import { Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from '@/shared/ui/tooltip'
import { useCurrentSubscription } from '../hooks/use-current-subscription'

/**
 * Topbar pill showing days left in the active trial. Renders only when the
 * club's lifecycle `status === 'trial'` and the trial is still in flight.
 *
 * Tone progression:
 *   > 14 days → neutral (info accent)
 *   ≤ 14 days → warn
 *   ≤ 3 days  → bad
 *
 * Click takes the owner to Settings → Suscripción so they can add a
 * payment method without hunting for the link. The chip stays on every
 * screen so the conversion path is always one click away — the biggest
 * lift for trial → paid in a SaaS funnel.
 *
 * Compact form: **icon + number only** (`✨ 57`) so the topbar stays
 * lean — the "días de prueba · ver suscripción" meaning lives in the
 * tooltip and the `aria-label` (a bare "57" button is unreadable to a
 * screen reader otherwise). Tone escalation + click target unchanged.
 */
export function TrialCountdownChip() {
  const sub = useCurrentSubscription()
  const navigate = useNavigate()
  if (!sub.isTrial || sub.isPartnerClub) return null
  if (sub.daysRemaining <= 0) return null

  const tone =
    sub.reminderThreshold === 3 ? 'bad' : sub.reminderThreshold === 14 ? 'warn' : 'info'

  const dayWord = sub.daysRemaining === 1 ? 'día' : 'días'
  const label = `${sub.daysRemaining} ${dayWord} de prueba · ver suscripción y método de pago`

  return (
    <Tooltip content={label}>
      <button
        type="button"
        className={`trial-chip trial-chip--${tone}`}
        aria-label={label}
        onClick={() => navigate('/settings?tab=subscription')}
      >
        <Sparkles size={14} aria-hidden="true" />
        <span className="mono">{sub.daysRemaining}</span>
      </button>
    </Tooltip>
  )
}
